import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          console.error('Error accessing localStorage:', error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          console.error('Error setting localStorage:', error);
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing from localStorage:', error);
        }
      }
    }
  }
});

// Helper function to get the current user's ID
export const getCurrentUserId = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  return user.id;
};

// Helper function to check if a user owns a resource
export const checkResourceOwnership = async (table: string, resourceId: string) => {
  const userId = await getCurrentUserId();
  const { data, error } = await supabase
    .from(table)
    .select('user_id')
    .eq('id', resourceId)
    .single();

  if (error) throw error;
  return data.user_id === userId;
};

export const saveTranscript = async (summaryId: number, content: string) => {
  try {
    const { data, error } = await supabase
      .from('transcripts')
      .insert([
        {
          summary_id: summaryId,
          content: content,
          user_id: (await supabase.auth.getUser()).data.user?.id
        }
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving transcript:', error);
    throw error;
  }
};

export const getTranscript = async (summaryId: number) => {
  try {
    const { data, error } = await supabase
      .from('transcripts')
      .select('content')
      .eq('summary_id', summaryId)
      .single();

    if (error) throw error;
    return data?.content;
  } catch (error) {
    console.error('Error fetching transcript:', error);
    throw error;
  }
};